
// Shared Logic for determining "Latest Version" and parsing source URLs
// See docs/COMPLIANCE_LATEST_VERSION_RULES.md for rules.

export type SourceKind = 'LAW' | 'GAZETTE' | 'MIXED' | 'OTHER';

export interface ComplianceSourceResult {
    latestDateText: string;    // Display text (e.g. "法：112/08/16...")
    latestDateISO: string | null; // For sorting
    announcementUrl: string;
    attachmentUrls: string[];
    methodNote: string;
    kind: SourceKind;
}

export function resolveComplianceSource(sourceTextOrUrl: string, kindHint?: SourceKind): ComplianceSourceResult {
    const input = sourceTextOrUrl.trim();
    let kind: SourceKind = kindHint || 'OTHER';
    
    // 1. Auto-detect Kind if not provided
    if (kind === 'OTHER') {
        if (input.includes('law.moj.gov.tw') || input.includes('法規')) kind = 'LAW';
        else if (input.includes('gazette') || input.includes('公報')) kind = 'GAZETTE';
        else if (input.includes('|') || input.includes('；') || input.includes(';')) kind = 'MIXED';
    }

    const result: ComplianceSourceResult = {
        latestDateText: "",
        latestDateISO: null,
        announcementUrl: input, // Default to input
        attachmentUrls: [],
        methodNote: "Manual/CSV Input",
        kind
    };

    // 2. Logic Implementation
    // If input looks like a date string (e.g. "113/07/31"), put it in dateText
    if (/^\d{2,3}\/\d{1,2}\/\d{1,2}/.test(input)) {
        result.latestDateText = input;
    }

    return result;
}

export function resolveAgencyName(url: string): string {
    if (!url) return "—";
    try {
        const u = new URL(url);
        const host = u.hostname.toLowerCase();
        if (host.includes("fda.gov.tw")) return "衛福部食藥署";
        if (host.includes("osha.gov.tw")) return "勞動部職安署";
        if (host.includes("mol.gov.tw")) return "勞動部";
        if (host.includes("moenv.gov.tw")) return "環境部";
        if (host.includes("mohw.gov.tw")) return "衛生福利部";
        if (host.includes("ndc.gov.tw")) return "國家發展委員會";
        if (host.includes("law.moj.gov.tw")) return "全國法規資料庫";
        if (host.includes("gazette.nat.gov.tw")) return "行政院公報";
        if (host.includes("bli.gov.tw")) return "勞保局";
        if (host.includes("ftracebook.fda.gov.tw")) return "食藥署(非追不可)";
    } catch (e) { 
        // ignore invalid url
    }
    return "—";
}

/**
 * Helper to map Department Name from text (e.g. from CSV) to Code
 */
export function mapOwnerDeptToCode(deptText: string): string {
    if (!deptText) return "";
    const txt = deptText.trim();
    
    // Explicit overrides based on keywords in the text (CSV content)
    if (txt.includes("資訊") || txt.includes("IT") || txt.includes("資安") || txt.includes("系統")) return "IT";
    if (txt.includes("行政人事") || txt.includes("人資") || txt.includes("人力")) return "AP";
    
    if (txt.includes("品保") || txt.includes("食安")) return "QC";
    if (txt.includes("財務") || txt.includes("會計")) return "FA";
    if (txt.includes("物流") || txt.includes("倉儲")) return "LI";
    if (txt.includes("採購")) return "PT"; // 採購部
    if (txt.includes("總經理") || txt.includes("GM")) return "GM";
    if (txt.includes("法務")) return "LG";
    if (txt.includes("業務")) return "SL1"; // Default to SL1 or make generic
    if (txt.includes("行銷") || txt.includes("廣告")) return "MK"; // 行銷宣傳部
    
    return ""; // Unknown
}
